import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <Link to="/register" style={{ textDecoration: "none" }}>
            <div style={styles.logoRow}>
              <div style={styles.logoMark}>M</div>
              <span style={styles.logoText}>MasterFileAI</span>
            </div>
          </Link>
        </div>

        <h1 style={styles.title}>Terms of Service</h1>
        <p style={styles.updated}>Last updated: March 17, 2026</p>

        <div style={styles.content}>
          <Section title="1. Acceptance of Terms">
            By creating an account or using MasterFileAI ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
          </Section>

          <Section title="2. Description of Service">
            MasterFileAI provides AI-powered data cleaning and enrichment for vendor and customer master data files. The Service processes uploaded files, applies AI enrichment pipelines, and returns cleaned output files. The first 10 records of each file are always processed free of charge.
          </Section>

          <Section title="3. Account Registration">
            You must provide accurate, complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately of any unauthorized use of your account. Each account represents a single company tenant; team members may be invited under your tenant.
          </Section>

          <Section title="4. Acceptable Use">
            You agree not to: upload files containing illegal content; attempt to reverse-engineer, scrape, or abuse the Service; share your account credentials; use the Service to process data you do not have the right to process; interfere with the Service's infrastructure or security.
          </Section>

          <Section title="5. Data and Privacy">
            Your uploaded files are processed solely to provide the Service. We do not sell, share, or use your data to train models or for any purpose other than delivering your cleaned output. Files are stored temporarily in Azure Blob Storage and may be deleted after 90 days. See our Privacy Policy for full details.
          </Section>

          <Section title="6. Payment and Billing">
            Processing beyond the free 10-record tier requires payment via Stripe. Pricing is calculated per record on a progressive tier basis with a minimum of $250 per file. All payments are non-refundable once processing begins. No subscription or contract is required — you pay per upload.
          </Section>

          <Section title="7. Intellectual Property">
            The Service, its AI models, processing pipeline, and output formats are the intellectual property of MasterFileAI. Your input data remains your property. Output files generated from your data are yours to use freely.
          </Section>

          <Section title="8. Service Availability">
            We strive for high availability but do not guarantee uninterrupted access. We reserve the right to perform maintenance, updates, or changes to the Service with or without notice.
          </Section>

          <Section title="9. Limitation of Liability">
            The Service is provided "as is." To the maximum extent permitted by law, MasterFileAI is not liable for any indirect, incidental, or consequential damages arising from your use of the Service, including data loss or processing errors. Our total liability shall not exceed the amount you paid in the 3 months prior to the claim.
          </Section>

          <Section title="10. Termination">
            Either party may terminate the relationship at any time. We reserve the right to suspend or terminate accounts that violate these Terms. Upon termination, your data will be deleted within 30 days.
          </Section>

          <Section title="11. Changes to Terms">
            We may update these Terms at any time. Continued use of the Service after changes constitutes acceptance of the updated Terms.
          </Section>

          <Section title="12. Governing Law">
            These Terms are governed by the laws of the jurisdiction in which MasterFileAI operates. Any disputes shall be resolved through binding arbitration.
          </Section>

          <Section title="13. Contact">
            For questions about these Terms, contact us at legal@masterfileai.com.
          </Section>
        </div>

        <div style={styles.footer}>
          <Link to="/register" style={styles.backLink}>← Back to registration</Link>
          <Link to="/privacy" style={styles.backLink}>Privacy Policy →</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1f2937", marginBottom: 8 }}>{title}</h2>
      <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.75 }}>{children}</p>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f8f9fb",
    padding: "40px 16px",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  container: {
    maxWidth: 720,
    margin: "0 auto",
    background: "#ffffff",
    border: "1px solid #e2e6ef",
    borderRadius: 16,
    padding: "48px 56px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  },
  header: { marginBottom: 32 },
  logoRow: { display: "flex", alignItems: "center", gap: 10 },
  logoMark: {
    width: 36, height: 36, borderRadius: 10,
    background: "linear-gradient(135deg, #6c63ff, #3ecfcf)",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontWeight: 700, fontSize: 18,
  },
  logoText: { color: "#111827", fontWeight: 700, fontSize: 18, letterSpacing: "-0.3px" },
  title: { fontSize: 28, fontWeight: 700, color: "#111827", margin: "0 0 6px" },
  updated: { fontSize: 13, color: "#9ca3af", margin: "0 0 36px" },
  content: { borderTop: "1px solid #e2e6ef", paddingTop: 32 },
  footer: {
    borderTop: "1px solid #e2e6ef",
    paddingTop: 24,
    marginTop: 16,
    display: "flex",
    justifyContent: "space-between",
  },
  backLink: { color: "#6c63ff", fontSize: 14, textDecoration: "none", fontWeight: 500 },
};
